import React, { useRef, useState, useEffect } from "react"
import copyIcon from "@/common/assets/image/products/transfer/ic-line-copy.svg"
import checkIcon from "@/common/assets/image/products/transfer/ic-line-check.svg"

const containerStyle: React.CSSProperties = {
  background: "#1e1e2e",
  borderRadius: "8px 8px 0 0",
  padding: 0,
  boxShadow: "0 4px 40px rgba(255,194,17,0.50)",
  color: "#fff",
  position: "relative",
  overflow: "hidden",
  border: "1px solid #2a2a3a",
  width: "90%",
  boxSizing: "border-box",
  minHeight: "12rem"
}

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: "8px 12px",
  background: "#1a1b26",
  borderBottom: "1px solid #2a2a3a"
}

const headerButtonStyle: React.CSSProperties = {
  width: "10px",
  height: "10px",
  borderRadius: "50%",
  marginRight: "8px"
}

const codeContainerStyle: React.CSSProperties = {
  display: "flex",
  width: "100%",
  overflowX: "auto",
  background: "#1e1e2e"
}

const lineNumbersStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  padding: "12px 0",
  background: "#1a1b26",
  color: "#565f89",
  userSelect: "none",
  textAlign: "right",
  borderRight: "1px solid #2a2a3a",
  minWidth: "40px",
  paddingRight: "8px",
  paddingLeft: "8px",
  fontFamily:
    "'Fira Mono', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  fontSize: "14px",
  lineHeight: "1.5"
}

const codeStyle: React.CSSProperties = {
  flex: 1,
  padding: "12px 16px",
  fontFamily:
    "'Fira Mono', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  fontSize: "0.875rem",
  color: "#c0caf5",
  lineHeight: "1.5",
  whiteSpace: "pre",
  overflow: "auto",
  scrollbarWidth: "thin", // Firefox
  scrollbarColor: "#FFC211 transparent" // Firefox
}

export default function CodeBlockContainer({
  children,
  label = "Source code"
}: {
  children: React.ReactNode
  label?: string
}) {
  const codeRef = useRef<HTMLDivElement>(null)
  const [copied, setCopied] = useState(false)
  const [lineCount, setLineCount] = useState(0)

  useEffect(() => {
    if (codeRef.current) {
      const text = codeRef.current.innerText
      const lines = text.split("\n")
      setLineCount(lines.length)
    }
  }, [children])

  const handleCopy = () => {
    if (codeRef.current) {
      const text = codeRef.current.innerText
      navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }

  const renderLineNumbers = () => {
    const numbers = []
    for (let i = 1; i <= lineCount; i++) {
      numbers.push(
        <div key={i} style={{ paddingRight: "8px" }}>
          {i}
        </div>
      )
    }
    return numbers
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ ...headerButtonStyle, background: "#ff5f56" }}></div>
        <div style={{ ...headerButtonStyle, background: "#ffbd2e" }}></div>
        <div style={{ ...headerButtonStyle, background: "#27c93f" }}></div>
      </div>
      <div style={codeContainerStyle}>
        <div style={lineNumbersStyle}>{renderLineNumbers()}</div>
        <div style={codeStyle} ref={codeRef}>
          <style>{`
            /* Chrome, Safari, Edge */
            .codeblock-scroll::-webkit-scrollbar {
              height: 2px;
              background: transparent;
            }
            .codeblock-scroll::-webkit-scrollbar-thumb {
              background: #FFC211;
              border-radius: 2px;
            }
            .codeblock-scroll::-webkit-scrollbar-track {
              background: transparent;
            }
          `}</style>
          <div className="codeblock-scroll" style={{ overflow: "auto" }}>
            {children}
          </div>
        </div>
        <button
          onClick={handleCopy}
          style={{
            position: "absolute",
            right: 12,
            bottom: 12,
            background: "rgba(53, 53, 53, 0.5)",
            border: "none",
            borderRadius: 6,
            padding: "10px 10px",
            color: "#c0caf5",
            cursor: "pointer",
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            zIndex: 2
          }}
          aria-label="Copy code"
        >
          {copied ? (
            <img
              src={checkIcon.src}
              alt="Check"
              style={{
                width: 16,
                height: 16,
                filter: "brightness(0) invert(1)"
              }}
            />
          ) : (
            <img
              src={copyIcon.src}
              alt="Copy"
              style={{
                width: 16,
                height: 16,
                filter: "brightness(0) invert(1)"
              }}
            />
          )}
        </button>
      </div>
    </div>
  )
}
